import { Box, Button, Checkbox, CircularProgress, Dialog, IconButton, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { attachmentThumbnails, getFileSizeFromBytes, Icons } from '../../utils/utilities'
import { useMeetingStore } from '../../store/meeting.store'
import { observer } from 'mobx-react'
import uploadFilesImg from '../../assets/upload-assignment.svg'
import api from '../../service/api'
import { useParams } from 'react-router-dom'
import TryAgainMessageBlock from '../common/TryAgainMessageBlock'
import CustomChip from '../common/CustomChip'
import { toast } from 'react-toastify'
import { useSocketsContext } from '../../contexts/sockets.context'
import { useVirtualboardStore } from '../../store/virtualboard.store'
import { useOnlineSessionsSocketStore } from '../../store/onlinesessionsocket.store'
import Progress from '../common/Progress'

/**
 * Polls the main server for job status
 * @param {string} jobId - The ID returned by the initial upload
 * @param {number} interval - Time between checks in ms (default 3s)
 * @param {number} maxAttempts - To prevent infinite loops (default 100)
 */
const pollJobStatus = ( jobId, interval = 1000, maxAttempts = 30, setProgress, meeting_id, presentation_id, job_id, file_id ) => {
    return new Promise( ( resolve, reject ) => {
        let attempts = 0

        const executePoll = async () => {
            try {
                const { data } = await api.onlineMeetings.getPresentationFileUploadStatus( meeting_id, presentation_id, job_id )

                const job = data.data

                setProgress( prev => ( { ...prev, [file_id]: { ...prev[file_id], status: job.status, progress: job.progress, uploading: false, converting: true } } ) )

                if ( job.status === 'completed' ) {
                    return resolve( job.data )
                }

                if ( job.status === 'failed' ) {
                    return reject( new Error( job.error || "Presentation file conversion failed" ) )
                }

                attempts++
                if ( attempts >= maxAttempts ) {
                    return reject( new Error( "Polling timed out" ) )
                }

                setTimeout( executePoll, interval )
            } catch ( err ) {
                console.log( err )
                reject( new Error( err?.response?.data?.message || "Failed to fetch presentation file status." ) )
            }
        }

        executePoll()
    } )
}

const PresentationsManager = observer( ( { container, toggleScreenShare } ) => {

    const [loadingPresentationFile, setLoadingPresentationFile] = useState( false )
    const [uploadStatuses, setUploadStatuses] = useState( [] )
    const [presentations, setPresentations] = useState( [] )
    const [dropping, setDropping] = useState( false )
    const [deleting, setDeleting] = useState( null )
    const [newFiles, setNewFiles] = useState( [] )
    const [error, setError] = useState( null )

    const { palette, border } = useTheme()
    const { meeting_id } = useParams()

    // const { emitEvent } = useSocketsContext()
    const OnlineSessionSocketStore = useOnlineSessionsSocketStore()

    const VirtualboardStore = useVirtualboardStore()
    const MeetingStore = useMeetingStore()

    const closePresentationManager = () => {
        MeetingStore.setMeetingRoomLayoutSettings( { isPrentationsManagerOpen: false } )
    }

    const onProgress = ( event, fileId ) => {
        setUploadStatuses( prev => ( { ...prev, [fileId]: { status: event.progress >= 1 ? "converting" : "uploading", progress: event.progress, uploading: event.progress < 1, converting: event.progress >= 1 } } ) )
    }

    const deletePresentationFile = async ( event, file ) => {
        try {
            event.stopPropagation()
            setDeleting( file.id )
            if ( file.id === MeetingStore.getMeetingState.presentingFileId ) {
                await OnlineSessionSocketStore.emit( 'presentation:stop' )
            }
            await api.onlineMeetings.deletePresentationFile( meeting_id, file.id )
            await fetchPresentations()
        } catch ( err ) {
            toast( err?.response?.data?.message || "Failed to delete the presentation file." )
        } finally {
            setDeleting( null )
        }
    }

    const loadSelectedPresentation = async ( fileId ) => {
        setLoadingPresentationFile( fileId )
        try {
            if ( MeetingStore.getMeetingState.isSharing )
                await toggleScreenShare()
            const presentation = presentations.find( p => p.id === fileId )
            const { data: { data: details } } = await api.onlineMeetings.fetchPresentationFileDetails( meeting_id, presentation.id )

            const document = {
                id: presentation.id,
                slides: details.file_pages.map( p => ( { id: p.id, url: p.url } ) ),
                name: details.file_details.name,
                currentSlide: 0,
                currentSlideId: details.file_pages?.[0]?.id,
            }
            const { success, presenter, elements, message } = await OnlineSessionSocketStore.emit( `presentation:set-new-presentation`, { room_id: meeting_id, presentation_id: presentation.id, presentation_document: document } )
            console.log( success, presenter, elements, message )


            if ( success ) {
                MeetingStore.setPresentingDocument( document )
                MeetingStore.setPresentingDocumentData( { elements } )

                VirtualboardStore.setBoardState( {
                    currentSlide: document.currentSlide,
                    slides: document.slides,
                    document: document,
                } )
                VirtualboardStore.setElements( elements || {} )
                MeetingStore.setMeetingState( { presentingVirtualBoard: true, presentingFileId: presentation.id || fileId, activePresenter: presenter } )
                closePresentationManager()
            } else {
                toast( message )
            }

        } catch ( err ) {
            console.log( err )

            toast( err?.response?.data?.message || "Failed to load urls for presentation pages!" )
        } finally {
            setLoadingPresentationFile( null )
        }
    }

    const stopSharingVirtualBoard = async () => {
        const { success, message } = await OnlineSessionSocketStore.emit( "virtualboard:stop", { room_id: meeting_id } )
        if ( success ) {
            MeetingStore.setMeetingState( { activePresenter: null, presentingVirtualBoard: false, presentingFileId: null } )
        } else if ( message ) {
            toast( message )
        }
    }

    const uploadNewFile = async ( file, setAsActive = false ) => {
        try {
            const formData = new FormData()
            formData.append( 'file', file )
            setUploadStatuses( prev => ( { ...prev, [file.id]: { ...prev[file.id], status: "uploading", progress: 0 } } ) )
            const { data: { data, presentationFile } } = await api.onlineMeetings.uploadMeetingPresentations( meeting_id, formData, ( event ) => onProgress( event, file.id ) )
            await pollJobStatus( data.jobId, 1000, 1000, setUploadStatuses, meeting_id, presentationFile.id, data.jobId, file.id )
            setUploadStatuses( prev => ( { ...prev, [file.id]: { ...prev[file.id], status: "uploaded", progress: 1, uploading: false, converting: false } } ) )
            setNewFiles( prev => {
                const newList = [...prev]
                const fileIndex = newList.findIndex( f => f.id === file.id )
                newList.splice( fileIndex, 1 )
                return newList
            } )
        } catch ( err ) {
            console.log( err )

            toast( err?.response?.data?.message || err?.message || "Failed to upload your presentation files." )
            setUploadStatuses( prev => ( { ...prev, [file.id]: { ...prev[file.id], status: "failed", progress: 0 } } ) )
        }
    }

    const handleFilesInput = async event => {
        let files = []
        let wrongFormats = []
        let largeFiles = []
        const fileUploadStatuses = {}
        for ( let i = 0; i < event.target.files.length; i++ ) {
            const file = event.target.files[i]
            if ( file.size > 100 * 1024 * 1024 ) {
                largeFiles.push( file )
            } else {
                if ( ["pptx", "docx", "pdf"].includes( file.name?.split( "." )?.pop()?.toLowerCase() ) ) {
                    file.id = new Date().getTime()
                    files.push( file )
                    fileUploadStatuses[file.id] = { status: "uploading", progress: 0, converting: false, uploading: true }
                } else
                    wrongFormats.push( file.name )
            }
        }
        setNewFiles( [...newFiles, ...files] )
        setUploadStatuses( { ...uploadStatuses, ...fileUploadStatuses } )
        const fileUploadRequests = files.map( file => uploadNewFile( file ) )
        if ( wrongFormats.length > 0 )
            toast( `Unsupported file format: ${wrongFormats.join( ", " )}` )
        if ( largeFiles.length > 0 )
            toast( `File too large (Maximum 100MB): ${largeFiles.map( file => `${file.name}(${getFileSizeFromBytes( file.size )})` ).join( ", " )}` )
        event.target.value = ""
        await Promise.all( fileUploadRequests )
        await fetchPresentations( presentations.length === 0 )
    }

    const removeFile = ( index ) => {
        const updatedFiles = [...newFiles]
        const updatedUploadStatuses = { ...uploadStatuses }
        delete updatedUploadStatuses[updatedFiles[index].id]

        updatedFiles.splice( index, 1 )

        setUploadStatuses( updatedUploadStatuses )
        setNewFiles( updatedFiles )
    }

    const fetchPresentations = useCallback( async ( setLastAsActive = false ) => {
        try {
            const { data: { data: userPresentations } } = await api.onlineMeetings.fetchMeetingPresentations( meeting_id )
            setPresentations( userPresentations )
            setError( null )
        } catch ( err ) {
            console.log( err )

            setError( { code: err?.response?.status || 500, message: err?.response?.data?.message } )
        }
    }, [meeting_id] )

    useEffect( () => {
        fetchPresentations()
    }, [fetchPresentations] )

    return (
        <Dialog container={container.current} slotProps={{ paper: { sx: { width: "95%", maxWidth: "1024px" } } }} open={MeetingStore.getMeetingRoomLayoutSettings.isPrentationsManagerOpen} >
            <Box padding="20px" display="flex" gap="20px" alignItems="center" justifyContent="space-between" borderBottom={border[1]} >
                <Box>
                    <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">Manage your presentations</Typography>
                    <Typography fontSize="12px" color='textSecondary' >As a presenter you have the ability to upload any Office document or PDF file. We recommend PDF file for best results. Please ensure that a presentation is selected using the checkbox on the left hand side.</Typography>
                </Box>
                <Box display="flex" gap="10px" alignItems="center">
                    <IconButton onClick={closePresentationManager}>
                        {Icons.default.CloseIcon}
                    </IconButton>
                </Box>
            </Box>
            <Box padding="20px">
                <Typography variant='h6' letterSpacing="0.1em" color='textSecondary' component="p" textTransform="uppercase" fontSize="12px">Your presentations</Typography>
                {!error && <Box>
                    <Box marginTop="10px" display="flex" flexDirection="column" gap="10px">
                        {presentations.map( presentation => {
                            const ext = presentation?.file_details?.name?.split( "." ).pop()
                            return (
                                <Box padding="5px 10px" borderRadius="10px" sx={{ cursor: "pointer" }} bgcolor={palette.contentBg} border={border[1]} display="flex" gap="10px" alignItems="center" key={presentation.id}>
                                    {MeetingStore.getMeetingState.presentingFileId === presentation.id && <Box fontSize="20px">
                                        {Icons.default.CheckboxChecked}
                                    </Box>}
                                    <Box marginTop="5px">
                                        <Box component="img" sx={{ objectFit: "fit" }} width="35px" src={attachmentThumbnails[ext] || attachmentThumbnails["default"]} alt={presentation.file_details.name} />
                                    </Box>
                                    <Box flexGrow={1}>
                                        <Typography component="p" variant='subtitle2'>{presentation.file_details.name}</Typography>
                                        <Box display="flex" flexWrap="wrap" gap="5px">
                                            <CustomChip tooltipTitle='Number of pages' value={`${presentation.file_details.pages} pages`} />
                                            {MeetingStore.getMeetingState.presentingFileId === presentation.id && <CustomChip color="primary" value="Presenting" />}
                                        </Box>
                                    </Box>
                                    {MeetingStore.getMeetingState.presentingFileId !== presentation.id && <Button disabled={Boolean( loadingPresentationFile )} disableElevation startIcon={loadingPresentationFile === presentation.id && <CircularProgress size={20} />} onClick={() => loadSelectedPresentation( presentation.id )} variant='contained' sx={{ borderRadius: "10px" }} >Share</Button>}
                                    {MeetingStore.getMeetingState.presentingFileId === presentation.id && <Button disabled={Boolean( loadingPresentationFile )} startIcon={Icons.default.BlockIcon} onClick={stopSharingVirtualBoard} color='greyed' variant='text' sx={{ borderRadius: "10px" }} >Stop Sharing</Button>}
                                    <IconButton onClick={( event ) => deletePresentationFile( event, presentation )} disabled={MeetingStore.getMeetingState.presentingFileId === presentation.id || deleting === presentation.id} color='error'>
                                        {deleting === presentation.id ? <CircularProgress size={20} /> : Icons.default.DeleteIconOutlined}
                                    </IconButton>
                                </Box>
                            )
                        } )}

                        {newFiles.map( ( file, index ) => {
                            const ext = file.name.split( "." ).pop()
                            return (
                                <Box key={index} padding="5px 10px" borderRadius="10px" sx={{ cursor: "pointer" }} bgcolor={palette.contentBg} border={border[1]} display="flex" gap="10px" alignItems="center">
                                    {uploadStatuses[file.id].status === "failed" ? <Typography fontSize="20px" color='error' display="flex" component="span" align="center">{Icons.default.ErrorIcon}</Typography> : <Checkbox disabled />}
                                    <Box marginTop="5px">
                                        <Box component="img" sx={{ objectFit: "fit" }} width="35px" src={attachmentThumbnails[ext] || attachmentThumbnails["default"]} alt={file.name} />
                                    </Box>
                                    <Box display="flex" flexGrow={1} justifyContent="space-between" gap="10px" alignItems="center">
                                        <Box>
                                            <Typography variant='subtitle2'>{file.name}</Typography>
                                            {uploadStatuses[file.id].status !== "failed" && <Typography display="flex" gap="10px" alignItems="center" variant='body2' fontWeight={300} fontSize="12px">
                                                {Boolean( uploadStatuses[file.id]?.progress ) && uploadStatuses[file.id]?.progress > 0 && <Progress progress={( uploadStatuses[file.id]?.progress || 0 ) * 100} trackWidth={1} color={palette.primary.main} indicatorWidth={1} size={20} />}
                                                {!uploadStatuses[file.id]?.progress && <CircularProgress size={20} />}
                                                <em>{uploadStatuses[file.id].status}... {uploadStatuses[file.id].progress > 0 ? `${Math.round( uploadStatuses[file.id].progress * 100 )}%` : ""}</em>
                                            </Typography>}
                                            {uploadStatuses[file.id].status === "failed" && <Typography display="flex" gap="10px" color='error' alignItems="center" variant='body2' fontWeight={300} fontSize="12px">
                                                <em>{uploadStatuses[file.id].status}</em>
                                            </Typography>}
                                        </Box>
                                        {uploadStatuses[file.id].status === "failed" && <IconButton onClick={() => removeFile( index )}>
                                            {Icons.default.CloseIcon}
                                        </IconButton>}
                                    </Box>
                                </Box>
                            )
                        } )}
                    </Box>

                    {presentations.length === 0 && <Box padding="20px" border={border[1]} marginTop="10px" borderRadius="20px" display="flex" alignItems="center" justifyContent="center">
                        <Typography textAlign="center" variant='subttile2' component="p">You haven't uploaded any presentations yet!</Typography>
                    </Box>}
                </Box>}
                {error && <TryAgainMessageBlock getData={fetchPresentations} err={error.message} code={error.code} parent='component' />}
            </Box>
            <Box padding="0 20px 20px 20px">
                <Box display="flex" flexDirection="column" sx={{ cursor: "pointer", bgcolor: palette.contentBg, position: "relative", border: `2px dashed ${palette.borderColor}`, borderRadius: "5px", padding: "20px", minHeight: "160px", ":hover": { borderColor: palette.text.secondary } }} >
                    <Box component="input" onDragLeave={() => setDropping( false )} onDrop={() => setDropping( false )} onDragEnter={() => setDropping( true )} accept='.pdf,.docx,.pptx,application/pdf' type="file" sx={{ position: "absolute", opacity: 0, top: 0, left: 0, right: 0, bottom: 0, cursor: "pointer" }} onChange={handleFilesInput} multiple />
                    <Box flexGrow={1} display="flex" gap="10px" flexDirection="column" alignItems="center">
                        <Box component="img" src={uploadFilesImg} alt="Upload assignment" width="120px" />
                        {!dropping && <Box display="flex" gap="5px" flexDirection="column" alignItems="center">
                            <Typography textAlign="center" variant="body1" fontWeight="600" >Upload files</Typography>
                            <Typography textAlign="center" variant="subtitle2" color="textSecondary">Drag and drop files here or   </Typography>
                            <Button variant='contained' sx={{ marginY: "10px", pointerEvents: "none" }} disableElevation color='secondary'>Browse</Button>
                            <Typography textAlign="center" variant="body2" >Accepted formats: <strong><em><span style={{ color: palette.secondary.main }}>.PDF,.PPTX and .DOCX</span></em></strong> </Typography>
                            <Typography textAlign="center" variant="subtitle2" color="textSecondary" fontSize="12px">Max Size: 100MB</Typography>
                        </Box>}
                        {dropping && <Box flexGrow={1} display="flex" gap="5px" flexDirection="column" justifyContent="center" alignItems="center">
                            <Typography sx={{ pointerEvents: "none" }} className='animation-shake' variant="h6" color="grey">Drop the presentation files here!</Typography>
                        </Box>}
                    </Box>
                </Box>
            </Box>

        </Dialog>
    )
} )

export default PresentationsManager
